import React, { useEffect, useRef, useState } from 'react';

import { BASE_URL } from '@/../config';
import { AuthFlowSession } from '@/helpers/sessionUtils';

const ALLOWED_PROVIDERS = ['google', 'microsoft'] as const;
type SocialProvider = (typeof ALLOWED_PROVIDERS)[number];

// Icon components for better performance and reusability
const GoogleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
    <path
      d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
      fill="#EA4335"
    />
    <path
      d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
      fill="#4285F4"
    />
    <path
      d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
      fill="#FBBC05"
    />
    <path
      d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
      fill="#34A853"
    />
  </svg>
);

const MicrosoftIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
    <rect x="2" y="2" width="9" height="9" fill="#F35325" />
    <rect x="13" y="2" width="9" height="9" fill="#81BC06" />
    <rect x="2" y="13" width="9" height="9" fill="#05A6F0" />
    <rect x="13" y="13" width="9" height="9" fill="#FFBA08" />
  </svg>
);

const SOCIAL_PROVIDERS = [
  {
    id: 'google' as const,
    name: 'Google',
    ariaLabel: 'Sign in with Google',
    icon: GoogleIcon,
  },
  {
    id: 'microsoft' as const,
    name: 'Microsoft',
    ariaLabel: 'Sign in with Microsoft',
    icon: MicrosoftIcon,
  },
] as const;

interface SocialLoginButtonsProps {
  /** Whether this is for Shopify authentication flow */
  isShopify?: boolean;
  /**
   * Optional callback invoked synchronously just before the provider redirect
   * fires (e.g. to persist state to sessionStorage before the page unloads).
   */
  onBeforeRedirect?: () => void;
}

/**
 * Social login buttons component supporting Google and Microsoft authentication
 * with optional Shopify flow integration.
 *
 * @param isShopify - Whether this is for Shopify authentication flow
 */
const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  isShopify = false,
  onBeforeRedirect,
}) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSocialLogin = (provider: SocialProvider) => {
    // Prevent multiple clicks while navigating
    if (isNavigating) {
      return;
    }

    // Validate provider
    if (!ALLOWED_PROVIDERS.includes(provider)) {
      console.error(`Invalid provider: ${provider}`);
      setSessionError('Invalid authentication provider');
      return;
    }

    // Validate BASE_URL for security
    if (
      !BASE_URL ||
      (!BASE_URL.startsWith('https://') &&
        !BASE_URL.startsWith('http://localhost'))
    ) {
      console.error('Invalid or missing BASE_URL');
      setSessionError('Authentication service unavailable');
      return;
    }

    // Clear any previous session errors
    setSessionError(null);

    // Set loading state
    setIsNavigating(true);

    // Set sessionStorage to track the authentication flow with enhanced error handling
    try {
      // Check if sessionStorage is available first
      if (!AuthFlowSession.isAvailable()) {
        throw new Error('Session storage is not available');
      }

      if (isShopify) {
        AuthFlowSession.setShopify();
      } else {
        AuthFlowSession.clear();
      }
    } catch (error) {
      console.warn('Failed to update session storage:', error);
      // Set a user-friendly error message
      setSessionError(
        'Session storage unavailable. Authentication will continue, but you may need to manually navigate after login.',
      );
      // Continue with authentication - don't block the flow
    }

    // Invoke the pre-redirect callback (e.g. to save onboarding state)
    onBeforeRedirect?.();

    // Small delay to ensure state updates are applied before navigation
    // This prevents race conditions where the page unloads before error state updates
    timeoutRef.current = setTimeout(() => {
      try {
        window.location.href = `${BASE_URL}/auth/${provider}`;
      } catch (navigationError) {
        console.error('Navigation failed:', navigationError);
        setIsNavigating(false);
        setSessionError(
          'Failed to redirect to authentication provider. Please try again.',
        );
      }
    }, 100);
  };

  return (
    <>
      {sessionError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">{sessionError}</p>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 text-gray-700 text-sm">
        {SOCIAL_PROVIDERS.map(provider => {
          const IconComponent = provider.icon;
          const isThisButtonLoading = isNavigating;

          return (
            <button
              key={provider.id}
              type="button"
              onClick={() => handleSocialLogin(provider.id)}
              disabled={isNavigating}
              aria-label={
                isThisButtonLoading
                  ? `Signing in with ${provider.name}...`
                  : provider.ariaLabel
              }
              aria-busy={isThisButtonLoading}
              aria-describedby={isShopify ? 'shopify-flow-notice' : undefined}
              className={`flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-colors ${
                isNavigating
                  ? 'opacity-75 cursor-not-allowed'
                  : 'hover:bg-gray-50'
              }`}
            >
              {isThisButtonLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              ) : (
                <IconComponent />
              )}
              {isThisButtonLoading ? `Signing in...` : provider.name}
            </button>
          );
        })}
        {isShopify && (
          <div id="shopify-flow-notice" className="sr-only">
            Authentication for Shopify integration
          </div>
        )}
      </div>
    </>
  );
};

export default SocialLoginButtons;
